import { useEffect } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useAttendance } from "../context/AttendanceContext";
import { useMeeting } from "../context/MeetingContext";
import { useAuth } from "../context/AuthContext";

/**
 * Hook to trigger notifications for attendance check-in/check-out
 * and meetings
 */
export function useAttendanceNotifications() {
    const { addNotification } = useNotifications();
    const { userAttendance } = useAttendance();
    const { userProfile } = useAuth();

    useEffect(() => {
        if (!userAttendance || userAttendance.length === 0) return;

        const today = new Date().toISOString().split("T")[0];
        const todayAttendance = userAttendance.find((a) => a.date === today);

        if (!todayAttendance) return;

        // Notification for check-in
        if (todayAttendance.checkIn && !todayAttendance.checkOut) {
            const checkInTime = new Date(todayAttendance.checkIn.timestamp?.toDate?.() || todayAttendance.checkIn.timestamp);
            const isLate = todayAttendance.checkIn.isLate;
            
            addNotification({
                type: "attendance",
                title: "Check-in Recorded",
                message: `You checked in at ${checkInTime.toLocaleTimeString()}${isLate ? " (Late)" : ""}`,
                recipientId: userProfile?.uid,
                scheduledTime: checkInTime,
            });
        }

        // Notification for check-out
        if (todayAttendance.checkOut) {
            const checkOutTime = new Date(todayAttendance.checkOut.timestamp?.toDate?.() || todayAttendance.checkOut.timestamp);
            
            addNotification({
                type: "attendance",
                title: "Check-out Recorded",
                message: `You checked out at ${checkOutTime.toLocaleTimeString()}`,
                recipientId: userProfile?.uid,
                scheduledTime: checkOutTime,
            });
        }
    }, [userAttendance, userProfile?.uid, addNotification]);
}

/**
 * Hook to trigger notifications for upcoming meetings
 */
export function useMeetingNotifications() {
    const { addNotification } = useNotifications();
    const { todayMeetings } = useMeeting();
    const { userProfile } = useAuth();
    
    useEffect(() => {
        if (!todayMeetings || todayMeetings.length === 0) return;

        const now = new Date();
        
        todayMeetings.forEach((meeting) => {
            // Parse meeting time
            const [hours, minutes] = meeting.time?.split(":").map(Number) || [0, 0];
            const meetingDate = new Date();
            meetingDate.setHours(hours, minutes, 0, 0);

            // Calculate time until meeting
            const minutesUntilMeeting = Math.round((meetingDate - now) / 60000);

            // Send notification 15 minutes before meeting
            if (minutesUntilMeeting > 0 && minutesUntilMeeting <= 15 && minutesUntilMeeting > 14) {
                // Check if user is a participant
                const isParticipant = meeting.participants?.some(
                    (p) => p.uid === userProfile?.uid || p.email === userProfile?.email
                );

                if (isParticipant || meeting.organizedBy === userProfile?.uid) {
                    addNotification({
                        type: "meeting",
                        title: `Meeting: ${meeting.title}`,
                        message: `Your meeting starts in 15 minutes at ${meeting.time}`,
                        recipientId: userProfile?.uid,
                        scheduledTime: meetingDate,
                        link: "/employee/meetings",
                    });
                }
            }

            // Send notification at meeting start time
            if (minutesUntilMeeting === 0) {
                const isParticipant = meeting.participants?.some(
                    (p) => p.uid === userProfile?.uid || p.email === userProfile?.email
                );

                if (isParticipant || meeting.organizedBy === userProfile?.uid) {
                    addNotification({
                        type: "meeting",
                        title: `Meeting Started: ${meeting.title}`,
                        message: `Your meeting is starting now!${meeting.link ? ` Join: ${meeting.link}` : ""}`,
                        recipientId: userProfile?.uid,
                        scheduledTime: meetingDate,
                        link: "/employee/meetings",
                    });
                }
            }
        });
    }, [todayMeetings, userProfile?.uid, userProfile?.email, addNotification]);
}

/**
 * Hook to setup a notification with a scheduled time
 * Useful for reminder notifications
 */
export function useScheduledNotification(reminderDate, title, message, recipientId) {
    const { addNotification } = useNotifications();

    useEffect(() => {
        if (!reminderDate || !recipientId) return;

        const reminder = new Date(reminderDate);
        const now = new Date();

        if (reminder <= now) return; // Don't set notification for past dates

        const timeout = setTimeout(() => {
            addNotification({
                type: "warning",
                title,
                message,
                recipientId,
                scheduledTime: reminder,
            });
        }, reminder - now);

        return () => clearTimeout(timeout);
    }, [reminderDate, title, message, recipientId, addNotification]);
}
